import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { COLLS } from '../../constants';

type PbView = 'created' | 'assigned';

const STAGE_COLORS: Record<string, string> = {
  Discovery: '#7C2D12', Proposal: '#312E81', Negotiation: '#065F46', Closing: '#1E3A8A',
};

export default function PlaybooksPanel() {
  const { playbooks, go, setActivePbIdx, toast, currentUser } = useApp();
  const [view, setView] = useState<PbView>('created');
  const [analyticsIdx, setAnalyticsIdx] = useState<number | null>(null);

  const createdPbs = playbooks.filter(pb => pb.createdBy === currentUser?.name);
  const assignedPbs = playbooks.filter(pb => pb.assignedToMe);
  const displayed = view === 'created' ? createdPbs : assignedPbs;

  return (
    <div style={{ paddingTop: '16px' }}>
      {/* View tabs */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
        {(['assigned', 'created'] as PbView[]).map(v => (
          <button key={v} onClick={() => setView(v)} style={{ padding: '8px 14px', borderRadius: '8px', border: '1.5px solid var(--border2)', background: view === v ? 'var(--navy)' : 'white', color: view === v ? 'white' : 'var(--text2)', fontFamily: 'inherit', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className={`ti ${v === 'assigned' ? 'ti-user-check' : 'ti-pencil'}`} />
            {v === 'assigned' ? 'Assigned to me' : 'Created by me'}
            <span style={{ background: view === v ? 'rgba(255,255,255,.2)' : 'var(--tag-bg)', color: view === v ? 'white' : 'var(--text3)', fontSize: '10px', fontWeight: '700', borderRadius: '20px', padding: '1px 7px' }}>
              {v === 'assigned' ? assignedPbs.length : createdPbs.length}
            </span>
          </button>
        ))}
      </div>

      <div style={{ fontSize: '11px', color: 'var(--text3)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <i className="ti ti-info-circle" />
        {view === 'assigned' ? 'Playbooks shared with you by others' : 'Playbooks you have created and sent'}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {displayed.map((pb, idx) => {
          const globalIdx = playbooks.indexOf(pb);
          const collaterals = (pb.colls || []).map(i => COLLS[i]).filter(Boolean);
          return (
            <div key={idx} style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
              {/* Deal room color bar */}
              <div style={{ height: '4px', background: pb.dealRoom.color }} />
              <div style={{ padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '15px', fontWeight: '700', color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pb.name}</span>
                      <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', background: pb.status === 'sent' ? '#F0FFF4' : 'var(--surface)', color: pb.status === 'sent' ? '#276749' : 'var(--text3)', border: `1px solid ${pb.status === 'sent' ? '#9AE6B4' : 'var(--border2)'}`, flexShrink: 0 }}>
                        {pb.status === 'sent' ? '✓ Sent' : '✎ Draft'}
                      </span>
                    </div>

                    {/* Deal room badge */}
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', borderRadius: '20px', background: pb.dealRoom.color + '1A', fontSize: '11px', fontWeight: '600', color: pb.dealRoom.accent, marginBottom: '10px' }}>
                      <i className={`ti ${pb.dealRoom.stageIco}`} style={{ fontSize: '11px' }} />
                      {pb.dealRoom.stage} · {pb.dealRoom.label}
                    </div>

                    {/* Recipients */}
                    {pb.recipients.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        {pb.recipients.map((r, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: 'var(--text2)' }}>
                            <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'var(--navy)', color: 'var(--gold)', fontSize: '8px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{r.ini}</div>
                            {r.name} · {r.org}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Collaterals strip */}
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                      {collaterals.map((c, i) => (
                        <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 9px', borderRadius: '6px', background: 'var(--surface)', border: '1px solid var(--border)', fontSize: '10px', color: 'var(--text2)', fontWeight: '500' }}>
                          <i className="ti ti-file" style={{ fontSize: '10px' }} /> {c.name.length > 22 ? c.name.substring(0, 22) + '…' : c.name}
                        </div>
                      ))}
                    </div>

                    {/* Stats */}
                    <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: 'var(--text3)' }}>
                      <span><strong style={{ color: 'var(--navy)' }}>{pb.views}</strong> views</span>
                      <span><strong style={{ color: 'var(--navy)' }}>{pb.opens}</strong> opens</span>
                      <span>Sent {pb.sent}</span>
                      <span>By {pb.createdBy}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 }}>
                    <button onClick={() => { setActivePbIdx(globalIdx); go('client'); }} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 12px', borderRadius: '7px', background: 'var(--navy)', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>
                      <i className="ti ti-eye" /> Preview
                    </button>
                    <button onClick={() => setAnalyticsIdx(analyticsIdx === globalIdx ? null : globalIdx)} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 12px', borderRadius: '7px', border: '1.5px solid var(--border2)', background: 'white', fontFamily: 'inherit', fontSize: '11px', color: 'var(--text2)', cursor: 'pointer' }}>
                      <i className="ti ti-chart-bar" /> Analytics
                    </button>
                    <button onClick={() => toast('Share link copied!')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 12px', borderRadius: '7px', border: '1.5px solid var(--border2)', background: 'white', fontFamily: 'inherit', fontSize: '11px', color: 'var(--text2)', cursor: 'pointer' }}>
                      <i className="ti ti-share" /> Share
                    </button>
                  </div>
                </div>

                {/* Analytics drawer */}
                {analyticsIdx === globalIdx && (
                  <div style={{ marginTop: '14px', padding: '14px', background: 'var(--surface)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--navy)', marginBottom: '12px' }}>Analytics — {pb.name}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px', marginBottom: '14px' }}>
                      {[{ label: 'Views', val: pb.views }, { label: 'Opens', val: pb.opens }, { label: 'Recipients', val: pb.recipients.length }, { label: 'Collaterals', val: collaterals.length }].map((k, i) => (
                        <div key={i} style={{ background: 'white', borderRadius: '8px', padding: '10px 12px', textAlign: 'center', border: '1px solid var(--border)' }}>
                          <div style={{ fontSize: '20px', fontWeight: '800', color: 'var(--navy)' }}>{k.val}</div>
                          <div style={{ fontSize: '10px', color: 'var(--text3)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.05em' }}>{k.label}</div>
                        </div>
                      ))}
                    </div>
                    {pb.recipients.map((r, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', background: 'white', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '6px' }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--navy)', color: 'var(--gold)', fontSize: '10px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{r.ini}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--navy)' }}>{r.name}</div>
                          <div style={{ fontSize: '10px', color: 'var(--text3)' }}>{r.org}</div>
                        </div>
                        <div style={{ fontSize: '10px', color: 'var(--text3)', textAlign: 'right' }}>
                          <div style={{ fontWeight: '600', color: 'var(--navy)' }}>{pb.views} views</div>
                          <div>Active</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
